import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, chartA11yProps } from '@respectly/ui';
import type { FeatureAdoptionItem } from '@respectly/shared-types';

interface FeatureAdoptionChartProps {
  data: FeatureAdoptionItem[];
  isLoading?: boolean;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3 shadow-lg text-xs">
      <p className="font-semibold text-slate-900 dark:text-slate-100 mb-1">{label}</p>
      <p className="text-slate-600 dark:text-slate-400">
        <span className="font-bold text-slate-900 dark:text-slate-100">{payload[0]?.value?.toLocaleString()}</span> activations
      </p>
    </div>
  );
};

export function FeatureAdoptionChart({ data, isLoading }: FeatureAdoptionChartProps) {
  if (isLoading) {
    return (
      <Card className="border-slate-200/60 dark:border-slate-700/60 h-full">
        <CardHeader className="pb-2">
          <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  const sorted = [...data].sort((a, b) => b.count - a.count);
  const maxCount = Math.max(...data.map((d) => d.count), 1);

  const chartLabel = `Feature adoption across all accounts, ranked by activations: ${sorted
    .map((d) => `${d.label} ${d.count.toLocaleString()}`)
    .join(', ')}.`;

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center">
            <div className="h-4 w-4 rounded-sm bg-blue-500" />
          </div>
          <div>
            <CardTitle className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Feature Adoption
            </CardTitle>
            <p className="text-xs text-slate-500 dark:text-slate-400">Usage across all accounts</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {maxCount === 0 ? (
          <div className="h-64 flex items-center justify-center">
            <p className="text-sm text-slate-400 dark:text-slate-500">No activity in this period</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={sorted}
              layout="vertical"
              margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
              {...chartA11yProps(chartLabel)}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                width={120}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(148,163,184,0.08)' }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={28}>
                {sorted.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.85} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
